// @flow
import { url as urlUtils } from '@clubmed/utils';

import { type Adults } from 'Store/trip-information/adults/adults.reducer';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { formatTimestampDate } from 'Utils/date/dateUtils';
import { DEFAULT_DEPARTURE_CITY_VALUE } from 'Utils/faceting/constants';
import { buildArgs } from 'Utils/graphql';
import gqlClient from 'Utils/graphql-client/graphqlClient';
import { STATE_PRODUCT_PRICE_OF_STAY } from 'Utils/request/requestDictionary';

export type PriceOfStayCriteria = {
  adults?: $ReadOnlyArray<Adults>,
  departureDate: number,
  departureCity: string,
  duration: number,
  kids?: $ReadOnlyArray<Kids>,
  productId: string,
  offerName?: string,
};

const getChildrenBirthdates = ({ kids = [] }: PriceOfStayCriteria): Array<String> =>
  // $FlowFixMe flow could not predict value after a filter and a map
  kids
    .filter(({ birthdate }) => !!birthdate)
    .map(kid => formatTimestampDate(kid.birthdate, API_DATE_FORMAT));

const getNumberAttendees = ({ adults = [], kids = [] }: PriceOfStayCriteria): number => {
  const kidsWithBirthdates = kids.filter(({ birthdate }) => !!birthdate);
  return adults.length + kidsWithBirthdates.length;
};

export type PriceOfStayError = {|
  errorCode: string,
  errorDescription: string,
|};

export type PriceOfStayData = {|
  accommodationId: string,
  accommodationLabel: string,
  bestPrice: {
    value: number,
    formattedValue: string,
  },
  currency: string,
  includesChildcare: boolean,
  includesTransportation: boolean,
  initialPrice: {
    value: number,
    formattedValue: string,
  },
  offerColor: string,
  resource: string,
  unit: {
    id: string,
    label: string,
  },
|};

export type PriceOfStayResponse = { priceOfStay: PriceOfStayData | {| errors: PriceOfStayError |} };

export const fetchPriceOfStay = (
  appContext: AppContext,
  searchCriteria: PriceOfStayCriteria,
): Promise<PriceOfStayResponse> => {
  const { productId, departureDate, duration, departureCity, offerName } = searchCriteria;
  const numberAttendees = getNumberAttendees(searchCriteria);

  const args = {
    productId,
    departureDate: formatTimestampDate(departureDate, API_DATE_FORMAT),
    duration,
    ...(numberAttendees && { numberAttendees }),
    // $FlowFixMe
    ...(numberAttendees && {
      childrenBirthdates: getChildrenBirthdates(searchCriteria),
    }),
    ...(departureCity !== DEFAULT_DEPARTURE_CITY_VALUE && { departureCity }),
    ...(offerName && { offerName: urlUtils.decodePath(offerName) }),
  };
  const argsString = buildArgs(args);

  const query = `query {
      priceOfStay(${argsString}) {
        ... on ErrorsPriceOfStayType {
          errors {
            errorCode
            errorDescription
          }
        }
        ... on PriceOfStayType {
          accommodationId
          accommodationLabel
          bestPrice {
            value
            formattedValue
          }
          currency
          includesChildcare
          includesTransportation
          initialPrice {
            value
            formattedValue
          }
          resource
          unit {
            id
            label
          }
          offerColor
        }
      }
    }`;

  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_PRODUCT_PRICE_OF_STAY,
  });
};
